import { useState, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useCourseGroupsOptions, useCoursesOptions } from "api/hooks";
import { useCourseRecipients, useCourseGroupRecipients } from "api/hooks/useRecipients";
import { useUsersOptions } from "api/hooks/useUsers";
import { EmailRecipientApi } from "api/types/EmailRecipient";
import {
  RecipientSelectionMode,
  RecipientData,
  UseRecipientSelectionConfig,
  UseRecipientSelectionReturn,
} from "./RecipientSelectorTypes";

export function useRecipientSelection<T extends RecipientData>(
  config: UseRecipientSelectionConfig<T>,
): UseRecipientSelectionReturn<T> {
  const { watch, setValue, getValues } = useFormContext();

  const [selectionMode, setSelectionMode] = useState<RecipientSelectionMode>("manual");
  const [manualRecipients, setManualRecipients] = useState<T[]>([]);
  const [pendingRecipients, setPendingRecipients] = useState<number[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // API hooks
  const { loading: courseGroupsLoading, options: courseGroupOptions } = useCourseGroupsOptions();
  const { isLoading: coursesLoading, options: coursesData } = useCoursesOptions();
  const { options: userOptions } = useUsersOptions();

  // Filter out recipients whose user option doesn't satisfy config.filterUserOption
  // (e.g. missing phone number). Applied to every path that adds recipients, not just
  // the manual-picker dropdown, so users without the required contact method never
  // enter state - they'd otherwise be silently dropped from the display but still
  // submitted to the backend.
  const filterEmailRecipients = (emailRecipients: EmailRecipientApi[]): EmailRecipientApi[] => {
    if (!config.filterUserOption) return emailRecipients;
    return emailRecipients.filter((recipient) => {
      const option = userOptions.find((opt) => parseInt(String(opt.value)) === recipient.id);
      return option ? config.filterUserOption!(option) : false;
    });
  };

  // Initialize with prepopulated recipients from config
  useEffect(() => {
    if (
      !isInitialized &&
      config.initialRecipientIds &&
      config.initialRecipientIds.length > 0 &&
      userOptions.length > 0
    ) {
      const initialRecipients = userOptions
        .filter((option) => config.initialRecipientIds!.includes(parseInt(String(option.value))))
        .filter((option) => !config.filterUserOption || config.filterUserOption(option))
        .map((option) => {
          const userId = parseInt(String(option.value));
          if (config.transformManualSelection) {
            return config.transformManualSelection(userId, option);
          }
          return userId as T;
        })
        .filter(Boolean) as T[];

      if (initialRecipients.length > 0) {
        setManualRecipients(initialRecipients);
        setSelectionMode("manual");
      }
      setIsInitialized(true);
    } else if (!isInitialized && (!config.initialRecipientIds || config.initialRecipientIds.length === 0)) {
      setIsInitialized(true);
    }
  }, [config.initialRecipientIds, userOptions, isInitialized, config]);

  // Filter out already selected recipients and apply custom filter
  const availableUserOptions = useMemo(() => {
    const selectedUserIds = manualRecipients.map(config.getUserId);
    let filtered = userOptions.filter((option) => !selectedUserIds.includes(parseInt(String(option.value))));

    if (config.filterUserOption) {
      filtered = filtered.filter(config.filterUserOption);
    }

    return filtered;
  }, [userOptions, manualRecipients, config]);

  const mode_courseGroup = watch("mode_courseGroup");
  const mode_course = watch("mode_course");

  const { recipients: courseRecipients, isFetching: courseRecipientsLoading } = useCourseRecipients(mode_course);
  const { recipients: courseGroupRecipients, isFetching: courseGroupRecipientsLoading } =
    useCourseGroupRecipients(mode_courseGroup);

  // Convert EmailRecipientApi to the desired type
  const convertRecipients = (emailRecipients: any[]): T[] => {
    return filterEmailRecipients(emailRecipients).map(config.convertFromEmailRecipient).filter(Boolean) as T[];
  };

  // Memoize current recipients based on selection mode
  const currentRecipients = useMemo(() => {
    switch (selectionMode) {
      case "course":
        return convertRecipients(courseRecipients);
      case "coursegroup":
        return convertRecipients(courseGroupRecipients);
      case "manual":
        return manualRecipients;
      default:
        return [];
    }
  }, [selectionMode, courseRecipients, courseGroupRecipients, manualRecipients]);

  // Update form when recipients change
  useEffect(() => {
    const currentFormValue = getValues(config.name) || [];

    // Only update if the values have actually changed to prevent infinite loops
    const hasChanged =
      currentRecipients.length !== currentFormValue.length ||
      JSON.stringify(currentRecipients) !== JSON.stringify(currentFormValue);

    if (hasChanged) {
      setValue(config.name, currentRecipients, { shouldValidate: true });
    }
  }, [currentRecipients, config.name, setValue, getValues]);

  // Handle mode change
  const handleModeChange = (mode: RecipientSelectionMode) => {
    setSelectionMode(mode);
  };

  // Handle manual recipient selection (store as pending)
  const handleManualRecipientsChange = (values: string[]) => {
    const recipientIds = values.map((v) => parseInt(v));
    setPendingRecipients(recipientIds);
  };

  // Confirm pending recipients and add them to manual selection
  const handleConfirmSelection = () => {
    const usersToAdd = userOptions
      .filter((option) => pendingRecipients.includes(parseInt(String(option.value))))
      .map((option) => {
        const userId = parseInt(String(option.value));
        if (config.transformManualSelection) {
          return config.transformManualSelection(userId, option);
        }
        // Default: just return the userId for simple number[] cases
        return userId as T;
      })
      .filter(Boolean) as T[];

    setManualRecipients((prev) => {
      const combined = [...prev, ...usersToAdd];
      // Remove duplicates by userId
      const unique = combined.filter(
        (recipient, index, arr) => arr.findIndex((r) => config.getUserId(r) === config.getUserId(recipient)) === index,
      );
      return unique;
    });
    setPendingRecipients([]);
  };

  // Remove individual recipient from manual selection
  const handleRemoveRecipient = (userId: number) => {
    setManualRecipients((prev) => prev.filter((r) => config.getUserId(r) !== userId));
  };

  // Add prepopulated recipients to manual selection
  const handleAddToManual = () => {
    const currentPrepopulated = selectionMode === "course" ? courseRecipients : courseGroupRecipients;
    const convertedRecipients = convertRecipients(currentPrepopulated);

    setManualRecipients((prev) => {
      const combined = [...prev, ...convertedRecipients];
      // Remove duplicates by userId
      const unique = combined.filter(
        (recipient, index, arr) => arr.findIndex((r) => config.getUserId(r) === config.getUserId(recipient)) === index,
      );
      return unique;
    });

    setSelectionMode("manual");
  };

  // Clear all recipients
  const handleClearRecipients = () => {
    setManualRecipients([]);
    setPendingRecipients([]);
  };

  const isLoading = courseRecipientsLoading || courseGroupRecipientsLoading;

  return {
    // State
    selectionMode,
    manualRecipients,
    pendingRecipients,

    // Computed values
    currentRecipients,
    isLoading,

    // Handlers
    handleModeChange,
    handleManualRecipientsChange,
    handleConfirmSelection,
    handleRemoveRecipient,
    handleAddToManual,
    handleClearRecipients,

    // Data for rendering
    courseOptions: coursesData,
    courseGroupOptions,
    availableUserOptions,
    coursesLoading,
    courseGroupsLoading,
    courseRecipientsLoading,
    courseGroupRecipientsLoading,
  };
}
